import * as L from 'leaflet';
// import 'leaflet.markercluster';

import { FeatureDisplayItem } from '../featureDisplay/featureDisplayItem';
import { FaMarker } from '../marker/faMarker/faMarker';
import { MoveMethod } from '../moveMethod.enum';
import { LayerWithMarkers } from './layerWithMarkers.interface';
import { MapLayer } from './mapLayer.abstract';

// https://github.com/Leaflet/Leaflet.markercluster
export class MarkerClusterOptions {
  chunkedLoading = true;
  showCoverageOnHover = true;
  disableClusteringAtZoom: number | undefined;
  spiderfyOnMaxZoom = true;
  zoomToBoundsOnClick = true;
  iconCreateFunction: (cluster: L.MarkerCluster) => L.DivIcon | L.Icon<L.IconOptions>;
}

type customClusterClickFuncType = null | ((markers: Array<L.Marker>, clickEvent: L.LeafletMouseEvent) => void);

export class MarkerLayer extends MapLayer implements LayerWithMarkers {
  protected markers = new Map<string, L.Marker>();
  protected clusterLayer: L.MarkerClusterGroup;
  protected zOffset: number;
  protected zOffsetApplyTimeout: NodeJS.Timeout;

  protected isClustered = false;
  protected clusterClickEnabled = false;
  protected customClusterClickFunc: customClusterClickFuncType;
  protected clusterOptions: MarkerClusterOptions;

  protected markerGroup: L.LayerGroup;

  protected defaultIcon: L.Icon | L.DivIcon;

  constructor(id: string, name?: string) {
    super(id, name);
    // Default options
    this.options.setOptions({
      pane: 'markerPane',
    });
  }

  public setDefaultIcon(icon: L.Icon | L.DivIcon): this {
    this.defaultIcon = icon;
    return this;
  }

  public addMarker(
    id: string,
    lat: number,
    lon: number,
    icon: null | L.Icon | L.DivIcon = null,
    tooltip: null | string | HTMLElement = null,
    popup: null | string | HTMLElement = null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    popupClick = (event) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    markerClick = (event) => {},
  ): this {
    const locObj = L.latLng(lat, lon);
    const marker = L.marker(locObj, { icon: this.getIcon(icon) } as L.MarkerOptions);

    return this.addLeafletMarker(id, marker, tooltip, popup, popupClick, markerClick);
  }

  public addLeafletMarker(
    id: string,
    marker: L.Marker,
    tooltip: null | string | HTMLElement = null,
    popup: null | string | HTMLElement = null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    popupClick = (event) => {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    markerClick = (event) => {},
  ): this {
    // only bind if not already bound.
    if (tooltip != null && marker.getTooltip() == null) {
      marker.bindTooltip(tooltip);
    }

    const displayItem = new FeatureDisplayItem(null, () => popup, popupClick);
    // add a reference so that it can be used on cluster click
     
    marker['featureDisplayItem'] = displayItem;

    // marker.bindPopup(popupElement);
    marker.on('click', (clickEvent: L.LeafletMouseEvent) => {
      const clickLatLng = [clickEvent.latlng.lat, clickEvent.latlng.lng] as [number, number];
      this.bgsLeaflet.getLayerClickManager()?.displayFeatures(Promise.resolve([displayItem]), clickLatLng);
    });

    marker.addEventListener('click', markerClick);

    this.markers.set(id, marker);
    return this;
  }

  public getMarkers(): Array<L.Marker> {
    return Array.from(this.markers.values());
  }

  public clearMarkers(): this {
    this.markers.clear();
    return this;
  }

  public setClustered(
    clustered = true,
    clusterOptions = new MarkerClusterOptions(),
    enableClusterClickFeatureIdentify = false,
    customClusterClickFunc: customClusterClickFuncType = null,
  ): this {
    this.isClustered = clustered;
    this.clusterClickEnabled = enableClusterClickFeatureIdentify;
    this.customClusterClickFunc = customClusterClickFunc;
    this.clusterOptions = {
      ...clusterOptions,
      zoomToBoundsOnClick: enableClusterClickFeatureIdentify ? false : clusterOptions.zoomToBoundsOnClick,
    };
    return this;
  }

  public getClusterLayer(): L.MarkerClusterGroup {
    const clusterLayer = L.markerClusterGroup(this.clusterOptions);
    // Cluster Layer click returns all the marker layers
    // let currentPopup;
    clusterLayer.on('clusterclick', (clickEvent: L.LeafletMouseEvent) => {
      if (this.clusterClickEnabled) {
        const targetLayer = (clickEvent as L.LayerEvent).layer as L.MarkerClusterGroup;
        const markers = targetLayer.getAllChildMarkers();
        if (null != this.customClusterClickFunc) {
          this.customClusterClickFunc(markers, clickEvent);
        } else {
          const clickLatLng = [clickEvent.latlng.lat, clickEvent.latlng.lng] as [number, number];
           
          const displayItems = markers.map((marker) => marker['featureDisplayItem']);

          this.bgsLeaflet.getLayerClickManager()?.displayFeatures(Promise.resolve(displayItems), clickLatLng);
        }
      }
    });
    return clusterLayer;
  }

  public getLeafletLayer(): Promise<L.Layer> {
    this.markerGroup = L.layerGroup([], this.options.getAll());
    this.markers.forEach((marker: L.Marker) => {
      // marker.on('click', () => this.setZOffset())
      this.markerGroup.addLayer(marker);
    });

    let leafletMapLayer: L.Layer;
    if (this.isClustered) {
      this.clusterLayer = this.getClusterLayer();
      leafletMapLayer = this.clusterLayer;
    } else {
      leafletMapLayer = L.layerGroup();
    }

    // set up event handlers to keep z indexes in check
    const map = this.bgsLeaflet.getLeafletObject();
    map.on('zoomend', () => this.setZOffset());
    map.on('moveeend', () => this.setZOffset());

    (leafletMapLayer as L.LayerGroup).addLayer(this.markerGroup);
    return Promise.resolve(leafletMapLayer);
  }

  /**
   *
   * @param markerId    // id of marker
   * @param mapConfig   // MapConfig object associated with this map
   * @param moveMethod  // MoveMethod e.g. PAN/ZOOM/FLY
   * @param zoom        // zoom level to set (not used in PAN move method)
   * @param popupDelay  // used to alter the delay between move-end and displaying the popup
   *                    // if the popup closers immediately, this needs increasing.
   */
  public goToMarker(markerId: string, moveMethod = MoveMethod.ZOOM, zoom?: number, popupDelay = 50): this {
    const marker = this.markers.get(markerId);
    if (marker != null) {
      this.bgsLeaflet.moveView(marker.getLatLng().lat, marker.getLatLng().lng, moveMethod, zoom);

      if (popupDelay != null && this.bgsLeaflet != null) {
        let timer;
        const leafletMap = this.bgsLeaflet.getLeafletObject();
        const moveEndOpenMarkerFunc = () => {
          // use of timeout and cleartimeout makes the timeout function inactive
          // for popupDelay, in case moveend is triggered multiple times
          // in quick succession.
          if (timer != null) {
            clearTimeout(timer);
          }
          timer = setTimeout(() => {
            leafletMap.off('moveend', moveEndOpenMarkerFunc);
            marker.openPopup();
          }, popupDelay);
        };
        leafletMap.on('moveend', moveEndOpenMarkerFunc);
      }
    }
    return this;
  }

  public setZOffset(index?: number): void {
    if (null != index) {
      this.zOffset = index * 100000;
    }

    clearTimeout(this.zOffsetApplyTimeout);

    this.zOffsetApplyTimeout = setTimeout(() => {
      if (null != this.zOffset && null != this.markers) {
        const markers = Array.from(this.markers.values());

        const displayedMarkers =
          null == this.clusterLayer
            ? markers
            : markers
                .map((marker) => this.clusterLayer.getVisibleParent(marker))
                .filter((marker) => null != marker) // not null
                .filter((marker, thisIndex: number, array) => thisIndex === array.indexOf(marker)); // unique
        // console.debug('setZOffset', this.zOffset, displayedMarkers);

        displayedMarkers.forEach((marker) => marker.setZIndexOffset(this.zOffset));
      }
    }, 100);
    // this.leafletMapLayer.on('zoom')
  }

  protected getIcon(iconOverride: null | L.Icon | L.DivIcon): L.Icon | L.DivIcon {
    if (iconOverride != null) {
      return iconOverride;
    } else if (this.defaultIcon != null) {
      return this.defaultIcon;
    } else {
      return new FaMarker().configureIcon(['fas', 'fa-circle'], '#ffffff');
    }
  }

  // only currently works on markers, not clusters
  protected updateLeafletLayerOpacity(): void {
    if (this.markers) {
      this.markers.forEach((marker: L.Marker) => {
        marker.setOpacity(this.options.opacity.get()!);
      });
    }
  }

  protected bringLayerToFront(refIndex: number): void {
    this.setZOffset(refIndex);
  }
  protected bringLayerToBack(refIndex: number): void {
    this.setZOffset(refIndex);
  }

  protected getMarkerElements(): Array<HTMLElement> {
     
    return Array.from(this.markers.values())
      .map((item) => item.getElement())
      .filter((item) => null != item) as Array<HTMLElement>;
  }
}
